
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Actions, ButtonLink, IconNewWindow } from 'braid-design-system';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "graphql"
    }}>{`GraphQL`}</h1>
    <p>{`The SEEK API uses `}<a parentName="p" {...{
        "href": "https://graphql.org/"
      }}>{`GraphQL`}</a>{` to expose a graph of `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionOpening"
      }}>{`positions`}</a>{`, `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/Candidate"
      }}>{`candidates`}</a>{` and other business objects.`}</p>
    <p>{`GraphQL allows you to query and join SEEK’s objects together in whatever way best suits your software.
It also gives SEEK insight into how its API is being used and which fields our partners are interested in.`}</p>
    <p>{`For more background on GraphQL we recommend the GraphQL Foundation’s `}<a parentName="p" {...{
        "href": "https://graphql.org/learn/"
      }}>{`Introduction to GraphQL`}</a>{`.`}</p>
    <h2 {...{
      "id": "code-samples"
    }}>{`Code samples`}</h2>
    <p>{`We provide GraphQL code samples across the Developer Site to demonstrate the implementation of features that are described in the documentation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  hiringOrganization(id: $id) {\\n    name\\n  }\\n}\",\"position\":{\"start\":{\"line\":14,\"column\":1,\"offset\":586},\"end\":{\"line\":20,\"column\":4,\"offset\":670},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":22,\"column\":1,\"offset\":672},\"end\":{\"line\":26,\"column\":4,\"offset\":742},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganization\\\": {\\n    \\\"name\\\": \\\"Acme Corp\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":28,\"column\":1,\"offset\":744},\"end\":{\"line\":34,\"column\":4,\"offset\":813},\"indent\":[1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "hiringOrganization(id:": true,
        "$id)": true,
        "name\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":14,\"column\":1,\"offset\":586},\"end\":{\"line\":20,\"column\":4,\"offset\":670},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":22,\"column\":1,\"offset\":672},\"end\":{\"line\":26,\"column\":4,\"offset\":742},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganization\\\":": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":28,\"column\":1,\"offset\":744},\"end\":{\"line\":34,\"column\":4,\"offset\":813},\"indent\":[1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  hiringOrganization(id: $id) {
    name
  }
}
`}</code></pre>
    <p>{`There are a few aspects to each code sample:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The first tab displays the query or mutation `}<a parentName="p" {...{
            "href": "/graphql/in-practice#operations"
          }}>{`operation`}</a>{` that is being described.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The second tab displays the input `}<a parentName="p" {...{
            "href": "/graphql/in-practice#variables"
          }}>{`variables`}</a>{` that are supplied alongside the operation.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The third tab displays the result of executing the operation with the supplied variables.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The Copy button copies the contents of the current tab to your clipboard.`}</p>
        <p parentName="li">{`This may be useful to incorporate the sample code into your software or an API testing tool.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The GraphQL Explorer button takes you to a `}<a parentName="p" {...{
            "href": "#dashboard"
          }}>{`dashboard`}</a>{` page where you can execute the operation.`}</p>
        <p parentName="li">{`The dashboard includes a lightweight, in-browser editor where you can execute GraphQL operations in one click.
It is similar in usage to API testing tools like Postman and Insomnia.`}</p>
        <p parentName="li">{`Our code samples reference `}<a parentName="p" {...{
            "href": "/graphql/playground/mock-objects"
          }}>{`mock objects`}</a>{` in our read-only `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` environment.
This allows us to document operations that can be executed by all partners;
by contrast, access to production objects is typically restricted to a single hirer or partner.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "dashboard"
    }}>{`Dashboard`}</h2>
    <p>{`The Developer Dashboard is an administrative user interface that lets you observe and configure your SEEK API integration:`}</p>

    <Actions mdxType="Actions">
  <ButtonLink href="https://manage.developer.seek.com" icon={<IconNewWindow mdxType="IconNewWindow" />} variant="soft" mdxType="ButtonLink">
    Dashboard
  </ButtonLink>
    </Actions>
    <p>{`It’s a key component of our `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`integration process`}</a>{` and can be used to:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Interact with `}<a parentName="p" {...{
            "href": "/use-cases/job-posting"
          }}>{`Job Posting`}</a>{` feature references`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Manage your `}<a parentName="p" {...{
            "href": "/events/webhooks"
          }}>{`webhook subscriptions`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Configure notification channels for release notes and system health`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Create and rotate your `}<a parentName="p" {...{
            "href": "/auth#client-credentials"
          }}>{`client credentials`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Run `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` operations in the GraphQL Explorer`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "schema"
    }}>{`Schema`}</h2>
    <p>{`The schema documentation website lets you inspect low-level details of our GraphQL operations & types:`}</p>
    <Actions mdxType="Actions">
  <ButtonLink href="https://developer.seek.com/schema" icon={<IconNewWindow mdxType="IconNewWindow" />} variant="soft" mdxType="ButtonLink">
    Schema
  </ButtonLink>
    </Actions>
    <p>{`This documentation is automatically generated from our GraphQL schema.
You can `}<a parentName="p" {...{
        "href": "https://graphql.org/learn/introspection/"
      }}>{`introspect`}</a>{` the schema yourself by pointing GraphQL client tooling at `}<inlineCode parentName="p">{`https://graphql.seek.com/graphql`}</inlineCode>{`.
See `}<a parentName="p" {...{
        "href": "/graphql/in-practice"
      }}>{`GraphQL in practice`}</a>{` for more information about our GraphQL endpoint and client tooling.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;